import { useState } from "react";

import StatCard, { type StatCardDelta } from "../../components/StatCard";
import type { OverviewResponse } from "../../api/types";

type OverviewData = Pick<
  OverviewResponse,
  | "month_liters"
  | "month_spend"
  | "month_km_driven"
  | "month_avg_l_per_100km"
  | "month_avg_cost_per_100km"
  | "last_month_liters"
  | "last_month_spend"
  | "last_month_km_driven"
  | "last_month_avg_l_per_100km"
  | "last_month_avg_cost_per_100km"
>;

function delta(current: number | null, previous: number | null): StatCardDelta | null {
  if (current == null || previous == null || previous === 0) return null;
  const pct = ((current - previous) / previous) * 100;
  if (Math.abs(pct) < 0.5) return { pct: 0, direction: "neutral" };
  return { pct, direction: pct > 0 ? "up" : "down" };
}

export default function StatCardGrid({
  month_liters,
  month_spend,
  month_km_driven,
  month_avg_l_per_100km,
  month_avg_cost_per_100km,
  last_month_liters,
  last_month_spend,
  last_month_km_driven,
  last_month_avg_l_per_100km,
  last_month_avg_cost_per_100km,
}: OverviewData) {
  const [fuelView, setFuelView] = useState<"liters" | "spend">("liters");
  const [consumptionView, setConsumptionView] = useState<"liters" | "cost">("liters");
  const showLiters = fuelView === "liters";
  const showConsumptionLiters = consumptionView === "liters";

  return (
    <section className="page-section">
      <header className="section-header">
        <h1 className="section-header__title">This month</h1>
      </header>

      <div className="card-grid card-grid--overview">
        <StatCard
          iconName="fuel"
          value={showLiters ? month_liters.toFixed(1) : month_spend.toFixed(2)}
          unit={showLiters ? "L" : "€"}
          label="Fuel"
          delta={
            showLiters
              ? delta(month_liters, last_month_liters)
              : delta(month_spend, last_month_spend)
          }
          onClick={() => setFuelView((v) => (v === "liters" ? "spend" : "liters"))}
          toggle={{ activeIndex: showLiters ? 0 : 1, count: 2 }}
        />

        <StatCard
          iconName="route"
          value={month_km_driven != null ? Math.round(month_km_driven).toLocaleString() : "—"}
          unit={month_km_driven != null ? "km" : ""}
          label="Distance"
          delta={delta(month_km_driven, last_month_km_driven)}
        />

        <StatCard
          iconName="chart"
          value={
            showConsumptionLiters
              ? month_avg_l_per_100km != null ? month_avg_l_per_100km.toFixed(1) : "—"
              : month_avg_cost_per_100km != null ? month_avg_cost_per_100km.toFixed(2) : "—"
          }
          unit={
            showConsumptionLiters
              ? month_avg_l_per_100km != null ? "L/100" : ""
              : month_avg_cost_per_100km != null ? "€/100" : ""
          }
          label="Efficiency"
          delta={
            showConsumptionLiters
              ? delta(month_avg_l_per_100km, last_month_avg_l_per_100km)
              : delta(month_avg_cost_per_100km, last_month_avg_cost_per_100km)
          }
          onClick={() => setConsumptionView((v) => (v === "liters" ? "cost" : "liters"))}
          toggle={{ activeIndex: showConsumptionLiters ? 0 : 1, count: 2 }}
        />
      </div>
    </section>
  );
}
